import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import html2canvas from 'html2canvas';

// Convert a data-URL to a Blob URL that browsers can display in a new tab
const openDataUrlInNewTab = (dataUrl) => {
  try {
    const [header, b64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  } catch {
    // Fallback: try direct open
    window.open(dataUrl, '_blank');
  }
};

// ── Category Options ────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'bug', label: '🐛 Bug Report', color: '#e53e3e' },
  { value: 'feature_request', label: '✨ Feature Request', color: '#805ad5' },
  { value: 'ui_ux', label: '🎨 UI/UX Issue', color: '#dd6b20' },
  { value: 'general', label: '💬 General Feedback', color: '#3182ce' },
];

// ── Component ───────────────────────────────────────────────────────────────

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [screenshot, setScreenshot] = useState(null);       // base64 PNG
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const panelRef = useRef(null);
  const location = useLocation();
  const { token } = useAuth();

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target)) {
        // Don't close if clicking the toggle button
        if (e.target.closest('[data-feedback-toggle]')) return;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Reset form after successful submit auto-close
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
        setDescription('');
        setCategory('general');
        setContactEmail('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  // Capture screenshot when panel opens
  const captureScreenshot = async () => {
    setCapturingScreenshot(true);
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.5,                      // half-res keeps it small
        logging: false,
        ignoreElements: (el) => {
          // Skip the feedback widget itself so it doesn't appear in the screenshot
          return el.hasAttribute?.('data-feedback-toggle') || el === panelRef.current;
        },
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshot(dataUrl);
    } catch (err) {
      console.warn('Screenshot capture failed:', err);
      // Non-critical — just proceed without screenshot
    } finally {
      setCapturingScreenshot(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const api = axios.create({ baseURL: API_URL });
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      await api.post('/feedback', {
        category,
        description: description.trim(),
        page_url: location.pathname + location.search,
        browser_info: navigator.userAgent,
        screen_width: window.innerWidth,
        contact_email: !token && contactEmail.trim() ? contactEmail.trim() : undefined,
        metadata: {
          screen_height: window.innerHeight,
          referrer: document.referrer || null,
          timestamp_local: new Date().toISOString(),
          screenshot: screenshot || null
        }
      });

      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many submissions. Please wait a bit before sending more feedback.');
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggleOpen = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    setError(null);
    if (willOpen) {
      // Capture screenshot immediately on open (before panel covers content)
      captureScreenshot();
    }
    if (submitted) {
      setSubmitted(false);
      setDescription('');
      setCategory('general');
      setScreenshot(null);
      setContactEmail('');
    }
  };

  return (
    <>
      {/* ── Floating Toggle Button ─────────────────────────────────────── */}
      <button
        data-feedback-toggle="true"
        onClick={toggleOpen}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9998,
          background: isOpen
            ? 'linear-gradient(135deg, #e53e3e, #c53030)'
            : 'linear-gradient(135deg, #667eea, #764ba2)',
          color: '#fff',
          border: 'none',
          borderRadius: '28px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          transform: 'scale(1)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        title={isOpen ? 'Close feedback' : 'Send us feedback'}
      >
        <span style={{ fontSize: '16px' }}>{isOpen ? '✕' : '💬'}</span>
        <span className="feedback-btn-label" style={{
          maxWidth: isOpen ? '60px' : '80px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}>
          {isOpen ? 'Close' : 'Feedback'}
        </span>
      </button>

      {/* ── Slide-out Panel ────────────────────────────────────────────── */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '24px',
          zIndex: 9999,
          width: '340px',
          maxHeight: 'calc(100vh - 120px)',
          backgroundColor: '#1a202c',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          padding: '18px 20px',
          flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '16px', fontWeight: '700' }}>
            💬 Send Us Feedback
          </h3>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
            We appreciate your input — it helps us improve!
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {submitted ? (
            /* ── Success State ────────────────────────────────────────── */
            <div style={{
              textAlign: 'center',
              padding: '24px 0',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
              <h4 style={{ color: '#fff', margin: '0 0 8px', fontSize: '16px' }}>
                Thank you!
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0 }}>
                Your feedback has been received. We'll review it shortly.
              </p>
            </div>
          ) : (
            /* ── Feedback Form ────────────────────────────────────────── */
            <form onSubmit={handleSubmit}>
              {/* Category */}
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '12px',
                fontWeight: '600',
                marginBottom: '6px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                Category
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px',
                marginBottom: '16px',
              }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '8px',
                      border: category === cat.value
                        ? `2px solid ${cat.color}`
                        : '2px solid rgba(255,255,255,0.1)',
                      backgroundColor: category === cat.value
                        ? `${cat.color}22`
                        : 'rgba(255,255,255,0.05)',
                      color: category === cat.value ? '#fff' : 'rgba(255,255,255,0.6)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      textAlign: 'left',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Screenshot Preview */}
              {(screenshot || capturingScreenshot) && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '6px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}>
                    📸 Page Screenshot
                  </label>
                  {capturingScreenshot ? (
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '12px',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                      ⏳ Capturing page...
                    </div>
                  ) : screenshot ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={screenshot}
                        alt="Page screenshot"
                        style={{
                          width: '100%',
                          borderRadius: '8px',
                          border: '2px solid rgba(255,255,255,0.1)',
                          cursor: 'pointer',
                        }}
                        onClick={() => openDataUrlInNewTab(screenshot)}
                        title="Click to view full size"
                      />
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                          borderRadius: '50%', width: '22px', height: '22px',
                          cursor: 'pointer', fontSize: '12px', lineHeight: '22px', textAlign: 'center',
                        }}
                        title="Remove screenshot"
                      >✕</button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Description */}
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '12px',
                fontWeight: '600',
                marginBottom: '6px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                Description <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell us what's on your mind..."
                required
                maxLength={10000}
                rows={5}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid rgba(255,255,255,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: '100px',
                  transition: 'border-color 0.15s ease',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#667eea'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px',
                marginBottom: '16px',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
                  {description.length} / 10,000
                </span>
              </div>

              {/* Contact Email (anonymous users only) */}
              {!token && (
                <>
                  <label style={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '6px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}>
                    Email <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', textTransform: 'none', fontWeight: '400' }}>(optional — for updates)</span>
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="your@email.com"
                    maxLength={255}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '2px solid rgba(255,255,255,0.1)',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      color: '#fff',
                      fontSize: '14px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      marginBottom: '16px',
                      transition: 'border-color 0.15s ease',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#667eea'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  />
                </>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  backgroundColor: 'rgba(229, 62, 62, 0.15)',
                  border: '1px solid rgba(229, 62, 62, 0.3)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  marginBottom: '12px',
                  color: '#feb2b2',
                  fontSize: '13px',
                }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || !description.trim()}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: submitting || !description.trim()
                    ? 'rgba(255,255,255,0.1)'
                    : 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: submitting || !description.trim()
                    ? 'rgba(255,255,255,0.3)'
                    : '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: submitting || !description.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {submitting ? '⏳ Sending...' : '🚀 Submit Feedback'}
              </button>

              {/* Privacy note */}
              <p style={{
                margin: '12px 0 0',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '11px',
                textAlign: 'center',
                lineHeight: '1.4',
              }}>
                Your current page and browser info will be included to help us diagnose issues.
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackWidget;
